/* ExpiringCredentialsPage — placement-centric dashboard of Bullhorn
 * credentials nearing or past expiry. A manual button per row hands off to
 * the existing "Request Credentials" flow for review; ticking rows and using
 * "Send requests" runs that same request/reminder en masse instead — one
 * request (and one email) per candidate covering all their ticked credentials.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldAlert, RefreshCw, Send, Settings as SettingsIcon, Filter, X, ArrowUp, ArrowDown, ArrowUpDown, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { complianceApi, credentialRequestsApi, getErrorMessage } from '../services/api';
import {
  EXPIRY_PRESETS,
  emptyExpiringCredentialsFilters,
  filterExpiringCredentials,
  sortExpiringCredentials,
  expiringCredentialsToCsv,
} from '../utils/expiringCredentialsFilters';

const emptyFilters = emptyExpiringCredentialsFilters;

function expiryBand(days) {
  if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, edge: 'border-l-red-500', text: 'text-red-600' };
  if (days <= 30) return { label: `${days} day${days === 1 ? '' : 's'}`, edge: 'border-l-amber-500', text: 'text-amber-600' };
  return { label: `${days} days`, edge: 'border-l-emerald-500', text: 'text-emerald-600' };
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function LastRequestCell({ lastRequest }) {
  if (!lastRequest) {
    return <span className="text-xs text-muted-foreground border border-dashed border-slate-300 rounded-full px-2 py-0.5 whitespace-nowrap">Not yet requested</span>;
  }
  const statusStyle = lastRequest.status === 'opened'
    ? 'bg-orange-50 text-orange-700 border-orange-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <div className="text-xs space-y-0.5">
      <span className={`inline-block rounded-full px-2 py-0.5 border whitespace-nowrap ${statusStyle}`}>
        Requested {formatDate(lastRequest.sent_at)} · {lastRequest.status}
      </span>
      {lastRequest.last_reminder_at && (
        <div className="text-muted-foreground whitespace-nowrap">Last reminder {formatDate(lastRequest.last_reminder_at)}</div>
      )}
    </div>
  );
}

// Placement has no name of its own in Bullhorn — the job title (plus company,
// if known) is the closest human-readable identifier, shown on hover so the
// bare ID list stays compact.
function placementTooltip(p) {
  return [p.job_title, p.company_name].filter(Boolean).join(' — ') || undefined;
}

function PlacementIds({ placements }) {
  if (!placements?.length) return null;
  return (
    <>
      {placements.map((p) => (
        <div key={p.id} title={placementTooltip(p)} className={placementTooltip(p) ? 'cursor-help underline decoration-dotted decoration-muted-foreground/50 w-fit' : undefined}>
          {p.id}
        </div>
      ))}
    </>
  );
}

// Stable identity for a row (the render key above includes the list index,
// which changes as filters/sorting move rows around — useless for selection).
const rowId = (row) => `${row.candidate_bullhorn_id}:${row.category_id}:${row.date_expiration}`;
// A request needs somewhere to go and a Bullhorn candidate to attach to.
const canSelect = (row) => !!row.candidate_email && !!row.candidate_bullhorn_id;

function SortableHeader({ label, sortKey, activeKey, dir, onSort, align, testId }) {
  const isActive = activeKey === sortKey;
  const Icon = isActive ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`inline-flex items-center gap-1 hover:text-foreground ${isActive ? 'text-foreground' : ''} ${align === 'center' ? 'justify-center w-full' : ''}`}
      data-testid={testId}
    >
      {label}
      <Icon className={`w-3 h-3 ${isActive ? 'opacity-100' : 'opacity-30'}`} />
    </button>
  );
}

export default function ExpiringCredentialsPage() {
  const navigate = useNavigate();
  const { getTenantPath } = useAuth();
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState(null); // null = not loaded, [] = enabled but empty
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const [sortKey, setSortKey] = useState('expiry');
  const [sortDir, setSortDir] = useState('asc');
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await complianceApi.getExpiring();
      setCredentials(data?.credentials || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load expiring credentials'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Distinct credential categories actually present in the loaded data,
  // alphabetised, for the Credential filter's options.
  const credentialOptions = useMemo(() => {
    const names = new Set((credentials || []).map((row) => row.category_name).filter(Boolean));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [credentials]);

  const filteredCredentials = useMemo(
    () => sortExpiringCredentials(filterExpiringCredentials(credentials, filters), sortKey, sortDir),
    [credentials, filters, sortKey, sortDir]
  );

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => value !== emptyFilters[key]).length;

  // ── Bulk send ─────────────────────────────────────────────────────────
  // Ticked rows (default none). Only rows currently visible count, so
  // changing a filter can never leave a hidden row quietly selected.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkSending, setBulkSending] = useState(false);

  const selectableRows = useMemo(() => filteredCredentials.filter(canSelect), [filteredCredentials]);
  const selectedRows = useMemo(() => selectableRows.filter((row) => selected.has(rowId(row))), [selectableRows, selected]);
  const allSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;

  const toggleRow = (row) => setSelected((prev) => {
    const next = new Set(prev);
    const id = rowId(row);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    for (const row of selectableRows) {
      if (allSelected) next.delete(rowId(row)); else next.add(rowId(row));
    }
    return next;
  });

  // One entry per candidate, so each gets a single email covering everything
  // ticked for them.
  const bulkGroups = useMemo(() => {
    const byCandidate = new Map();
    for (const row of selectedRows) {
      if (!byCandidate.has(row.candidate_bullhorn_id)) {
        byCandidate.set(row.candidate_bullhorn_id, { candidate: row, categories: new Map() });
      }
      const group = byCandidate.get(row.candidate_bullhorn_id);
      // A reminder if it already has an open request, otherwise a new request —
      // shown in the confirmation below; the server makes the final call.
      group.categories.set(String(row.category_id), { name: row.category_name, reminder: !!row.open_request_id });
    }
    return [...byCandidate.values()];
  }, [selectedRows]);

  const handleBulkSend = async () => {
    setBulkSending(true);
    try {
      // The server decides per credential: re-notify an existing open request
      // (a real reminder) or create a new one. One email per candidate either way.
      const { data } = await credentialRequestsApi.bulkSend({
        candidates: bulkGroups.map(({ candidate, categories }) => ({
          candidate_email: candidate.candidate_email,
          candidate_name: candidate.candidate_name || null,
          candidate_bullhorn_id: parseInt(candidate.candidate_bullhorn_id, 10),
          items: [...categories].map(([id, c]) => ({ bullhorn_category_id: id, bullhorn_category_name: c.name, required: true })),
        })),
        custom_message: bulkMessage.trim() || null,
      });
      const results = data?.results || [];
      const failed = results.filter((r) => r.error);
      const okEmails = new Set(results.filter((r) => !r.error).map((r) => r.candidate_email.toLowerCase()));
      const created = results.reduce((n, r) => n + (r.created || 0), 0);
      const reminded = results.reduce((n, r) => n + (r.reminded || 0), 0);
      // Done rows leave the selection; failed ones stay ticked so they can be retried.
      const doneIds = new Set(selectedRows.filter((r) => okEmails.has((r.candidate_email || '').toLowerCase())).map(rowId));
      setSelected((prev) => new Set([...prev].filter((id) => !doneIds.has(id))));
      if (okEmails.size) {
        const parts = [
          created ? `${created} new request${created === 1 ? '' : 's'}` : null,
          reminded ? `${reminded} reminder${reminded === 1 ? '' : 's'}` : null,
        ].filter(Boolean);
        toast.success(`Sent ${parts.join(' and ')} to ${okEmails.size} candidate${okEmails.size === 1 ? '' : 's'}`);
      }
      if (failed.length) {
        toast.error(`${failed.length} could not be sent — ${failed.map((r) => `${r.candidate_email}: ${r.error}`).join('; ')}`);
      } else {
        setBulkOpen(false);
        setBulkMessage('');
      }
      if (okEmails.size) load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not send the requests'));
    } finally {
      setBulkSending(false);
    }
  };

  const [remindingKey, setRemindingKey] = useState(null);
  const sendFor = async (row, isReminder, key) => {
    // A reminder re-notifies the request that's already open — it doesn't
    // create another one — so there's nothing to review: just send it.
    if (isReminder) {
      setRemindingKey(key);
      try {
        const { data } = await credentialRequestsApi.remindMany([row.open_request_id]);
        // The server says what actually happened — don't claim success if it
        // sent nothing (e.g. the request was cancelled or completed meanwhile).
        if (!data?.candidates_notified) {
          toast.error(data?.skipped_not_open
            ? 'That request is no longer open — refreshing so you can send a new request instead.'
            : 'The reminder email could not be sent.');
        } else {
          toast.success(`Reminder sent to ${row.candidate_name || row.candidate_email} — ${row.category_name}`);
        }
        load();
      } catch (err) {
        toast.error(getErrorMessage(err, 'Could not send the reminder'));
      } finally {
        setRemindingKey(null);
      }
      return;
    }
    navigate(getTenantPath('/send/credentials'), {
      state: {
        prefill: {
          candidateEmail: row.candidate_email,
          candidateName: row.candidate_name,
          bullhornCandidateId: row.candidate_bullhorn_id,
          categoryId: row.category_id,
          categoryName: row.category_name,
        },
      },
    });
  };

  const exportCsv = () => {
    if (!filteredCredentials.length) return;
    const csv = expiringCredentialsToCsv(filteredCredentials);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `expiring-credentials-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="max-w-[1400px] mx-auto py-6 space-y-6" data-testid="expiring-credentials-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-red-600 flex items-center justify-center">
              <ShieldAlert className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold">Expiring Credentials</h1>
              <p className="text-sm text-muted-foreground">
                Bullhorn credentials nearing or past expiry, for candidates on a qualifying placement.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="expiring-credentials-refresh">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {!loading && !error && credentials?.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={exportCsv}
                disabled={!filteredCredentials.length}
                title="Exports the rows currently shown, respecting active filters"
                data-testid="expiring-credentials-export-csv"
              >
                <Download className="w-4 h-4 mr-1.5" /> Export CSV
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(getTenantPath('/settings?section=compliance'))}
              data-testid="expiring-credentials-settings-link"
            >
              <SettingsIcon className="w-4 h-4 mr-1.5" /> Configure
            </Button>
          </div>
        </div>

        {loading && credentials === null && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" /> Scanning Bullhorn…
          </p>
        )}

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-4" data-testid="expiring-credentials-error">
            {error}
          </div>
        )}

        {!loading && !error && credentials?.length === 0 && (
          <div className="text-sm text-muted-foreground bg-slate-50 border border-slate-200 rounded-lg p-6 text-center" data-testid="expiring-credentials-empty">
            Nothing to show. Either everything's up to date, or this dashboard hasn't been configured yet —
            check <button className="underline" onClick={() => navigate(getTenantPath('/settings?section=compliance'))}>Settings → Compliance</button> to
            enable it and pick which placement statuses and credential categories to include.
          </div>
        )}

        {!loading && !error && credentials?.length > 0 && (
          <div className="rounded-xl border border-border bg-white" data-testid="expiring-credentials-filters">
            <div className="p-4 flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Candidate</Label>
                <Input
                  value={filters.candidate}
                  onChange={(e) => setFilter('candidate', e.target.value)}
                  placeholder="Name or email…"
                  className="h-9 w-48 text-sm"
                  data-testid="expiring-credentials-filter-candidate"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Placement ID</Label>
                <Input
                  value={filters.placement}
                  onChange={(e) => setFilter('placement', e.target.value)}
                  placeholder="e.g. 5378…"
                  className="h-9 w-32 text-sm"
                  data-testid="expiring-credentials-filter-placement"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Credential</Label>
                <Select value={filters.credential} onValueChange={(v) => setFilter('credential', v)}>
                  <SelectTrigger className="h-9 w-44 text-sm" data-testid="expiring-credentials-filter-credential">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All credentials</SelectItem>
                    {credentialOptions.map((name) => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">In HyreDocs</Label>
                <Select value={filters.inHyredocs} onValueChange={(v) => setFilter('inHyredocs', v)}>
                  <SelectTrigger className="h-9 w-32 text-sm" data-testid="expiring-credentials-filter-in-hyredocs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Last request</Label>
                <Select value={filters.lastRequest} onValueChange={(v) => setFilter('lastRequest', v)}>
                  <SelectTrigger className="h-9 w-40 text-sm" data-testid="expiring-credentials-filter-last-request">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="not_requested">Not yet requested</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="opened">Opened</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Expiry</Label>
                <Select value={filters.expiryPreset} onValueChange={(v) => setFilter('expiryPreset', v)}>
                  <SelectTrigger className="h-9 w-40 text-sm" data-testid="expiring-credentials-filter-expiry-preset">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_PRESETS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {filters.expiryPreset === 'custom' && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">From</Label>
                    <Input
                      type="date"
                      value={filters.expiryFrom}
                      onChange={(e) => setFilter('expiryFrom', e.target.value)}
                      className="h-9 w-36 text-sm"
                      data-testid="expiring-credentials-filter-expiry-from"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">To</Label>
                    <Input
                      type="date"
                      value={filters.expiryTo}
                      onChange={(e) => setFilter('expiryTo', e.target.value)}
                      className="h-9 w-36 text-sm"
                      data-testid="expiring-credentials-filter-expiry-to"
                    />
                  </div>
                </>
              )}
              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFilters(emptyFilters)}
                  className="text-muted-foreground"
                  data-testid="expiring-credentials-clear-filters"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Clear filters
                </Button>
              )}
            </div>
            <div className="px-4 pb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Filter className="w-3 h-3" />
              Showing {filteredCredentials.length} of {credentials.length}
            </div>
          </div>
        )}

        {!loading && !error && selectedRows.length > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-primary/30 bg-primary/5 px-4 py-2.5" data-testid="expiring-credentials-bulk-bar">
            <span className="text-sm">
              <strong>{selectedRows.length}</strong> credential{selectedRows.length === 1 ? '' : 's'} selected
              <span className="text-muted-foreground"> · {bulkGroups.length} candidate{bulkGroups.length === 1 ? '' : 's'}</span>
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="expiring-credentials-bulk-clear">
                Clear selection
              </Button>
              <Button size="sm" onClick={() => setBulkOpen(true)} data-testid="expiring-credentials-bulk-send">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Send requests ({selectedRows.length})
              </Button>
            </div>
          </div>
        )}

        {!loading && !error && credentials?.length > 0 && (
          <div className="rounded-xl border border-border overflow-hidden bg-white" data-testid="expiring-credentials-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2.5 w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = selectedRows.length > 0 && !allSelected; }}
                        onChange={toggleAll}
                        disabled={selectableRows.length === 0}
                        title="Select all rows currently shown"
                        aria-label="Select all"
                        data-testid="expiring-credentials-select-all"
                      />
                    </th>
                    <th className="text-left px-4 py-2.5 font-medium">
                      <SortableHeader label="Candidate" sortKey="candidate" activeKey={sortKey} dir={sortDir} onSort={handleSort} testId="expiring-credentials-sort-candidate" />
                    </th>
                    <th className="text-center px-4 py-2.5 font-medium">
                      <SortableHeader label="In HyreDocs" sortKey="inHyredocs" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="center" testId="expiring-credentials-sort-in-hyredocs" />
                    </th>
                    <th className="text-left px-4 py-2.5 font-medium">
                      <SortableHeader label="Placement ID(s)" sortKey="placement" activeKey={sortKey} dir={sortDir} onSort={handleSort} testId="expiring-credentials-sort-placement" />
                    </th>
                    <th className="text-left px-4 py-2.5 font-medium">
                      <SortableHeader label="Credential" sortKey="credential" activeKey={sortKey} dir={sortDir} onSort={handleSort} testId="expiring-credentials-sort-credential" />
                    </th>
                    <th className="text-left px-4 py-2.5 font-medium">
                      <SortableHeader label="Expiry" sortKey="expiry" activeKey={sortKey} dir={sortDir} onSort={handleSort} testId="expiring-credentials-sort-expiry" />
                    </th>
                    <th className="text-left px-4 py-2.5 font-medium">
                      <SortableHeader label="Last request" sortKey="lastRequest" activeKey={sortKey} dir={sortDir} onSort={handleSort} testId="expiring-credentials-sort-last-request" />
                    </th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCredentials.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="expiring-credentials-no-matches">
                        No credentials match the current filters.
                      </td>
                    </tr>
                  )}
                  {filteredCredentials.map((row, index) => {
                    const band = expiryBand(row.days_until_expiry);
                    // A candidate can hold more than one certification record in the
                    // same category (renewed after expiry) — date_expiration disambiguates
                    // those; index is a last-resort tie-break for genuine exact duplicates.
                    // Without this, React key collisions caused stale rows to survive
                    // filtering/sorting instead of being removed or reordered.
                    const key = `${row.candidate_bullhorn_id}:${row.category_id}:${row.date_expiration}:${index}`;
                    // A reminder only makes sense while a request is still open;
                    // once it's cancelled or done, the next one is a new request.
                    const isReminder = !!row.open_request_id;
                    return (
                      <tr key={key} className={`border-t border-border border-l-4 ${band.edge} ${selected.has(rowId(row)) && canSelect(row) ? 'bg-primary/5' : ''}`} data-testid={`expiring-credentials-row-${key}`}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={canSelect(row) && selected.has(rowId(row))}
                            onChange={() => toggleRow(row)}
                            disabled={!canSelect(row)}
                            title={canSelect(row) ? undefined : 'No email address on file for this candidate, so a request can\'t be sent'}
                            aria-label={`Select ${row.candidate_name || 'candidate'} — ${row.category_name}`}
                            data-testid={`expiring-credentials-select-${key}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{row.candidate_name || 'Unnamed candidate'}</div>
                          <div className="text-xs text-muted-foreground">{row.candidate_email}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!!row.in_hyredocs}
                            disabled
                            title={row.in_hyredocs ? 'Has a document on file in HyreDocs' : 'Known only via Bullhorn — no HyreDocs document on file'}
                            data-testid={`expiring-credentials-in-hyredocs-${key}`}
                          />
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                          <PlacementIds placements={row.placements} />
                        </td>
                        <td className="px-4 py-3">{row.category_name}</td>
                        <td className={`px-4 py-3 font-semibold ${band.text}`}>{band.label}</td>
                        <td className="px-4 py-3"><LastRequestCell lastRequest={row.last_request} /></td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant={isReminder ? 'outline' : 'default'}
                            onClick={() => sendFor(row, isReminder, key)}
                            disabled={remindingKey === key}
                            data-testid={`expiring-credentials-send-${key}`}
                          >
                            {remindingKey === key
                              ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              : <Send className="w-3.5 h-3.5 mr-1.5" />}
                            {isReminder ? 'Send reminder' : 'Send request'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={bulkOpen} onOpenChange={(open) => { if (!bulkSending) setBulkOpen(open); }}>
        <DialogContent className="max-w-lg" data-testid="expiring-credentials-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Send credential requests</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            A credential that already has an open request gets a <strong>reminder</strong> (no duplicate request); anything
            else gets a new request. A candidate with both receives two emails: one for the new requests, one reminder.
          </p>
          <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
            {bulkGroups.map((group) => (
              <div key={group.candidate.candidate_bullhorn_id} className="px-3 py-2 text-sm">
                <div className="font-medium">{group.candidate.candidate_name || 'Unnamed candidate'}
                  <span className="text-xs text-muted-foreground font-normal"> · {group.candidate.candidate_email}</span>
                </div>
                <ul className="mt-1 space-y-0.5">
                  {[...group.categories].map(([id, c]) => (
                    <li key={id} className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">{c.name}</span>
                      <span className={`rounded-full px-1.5 py-0.5 border text-[10px] font-medium ${c.reminder ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                        {c.reminder ? 'Reminder' : 'New request'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Message to candidates (optional)</Label>
            <Textarea
              value={bulkMessage}
              onChange={(e) => setBulkMessage(e.target.value)}
              rows={3}
              placeholder="Hi! Please upload these so we can finalise your placement…"
              data-testid="expiring-credentials-bulk-message"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkSending}>Cancel</Button>
            <Button onClick={handleBulkSend} disabled={bulkSending || bulkGroups.length === 0} data-testid="expiring-credentials-bulk-confirm">
              {bulkSending
                ? <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</>
                : <><Send className="w-4 h-4 mr-1.5" /> Send to {bulkGroups.length} candidate{bulkGroups.length === 1 ? '' : 's'}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
